import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Center,
  Flex,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Ban, Home } from 'lucide-react';
import {
  CHAT_PIN_LIMIT,
  DISAPPEARING_SECONDS,
  type DisappearingDuration,
  type WsConversationMembersPayload,
  type WsMessageDeletedPayload,
  EVERYONE_MENTION,
  isChatMuted,
  liveMutes,
  muteEndsAt,
  WS_EVENTS,
  type ChatSettings,
} from '@twinden/shared';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { sendBlessing } from '../components/chat/blessing';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  blessingWordsAtom,
  currentUserAtom,
  openConversationIdAtom,
  pushPreferencesAtom,
  typingUsersAtom,
} from '../store';
import { api, ApiError, type FamilyMember } from '../lib/api';
import { updateAppBadge } from '../lib/pwa';
import {
  useConversationMessages,
  useFamily,
  useNavAction,
  useWebSocket,
} from '../hooks';
import {
  GroupSettingsDialog,
  NewChatDialog,
  NewGroupDialog,
  type ChatConversation,
} from '../components/chat/ConversationDialogs';
import { ConversationList } from '../components/chat/ConversationList';
import { ChatHeader } from '../components/chat/ChatHeader';
import {
  MessageList,
  type MessageListHandle,
} from '../components/chat/MessageList';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { useLoadingState } from '../hooks/useLoadingState';
import {
  MessageComposer,
  type ComposerMode,
} from '../components/chat/MessageComposer';
import {
  ConversationSearch,
  type SearchHit,
} from '../components/chat/ConversationSearch';
import { MessageInfoSheet } from '../components/chat/MessageInfoSheet';
import {
  draftsAtom,
  loadDrafts,
  openDrafts,
  pruneDrafts,
  replyTargetGone,
} from '../lib/drafts';
import { DeleteMessageDialog } from '../components/chat/DeleteMessageDialog';
import { ForwardDialog } from '../components/chat/ForwardDialog';
import { MessageActionSheet } from '../components/chat/MessageActionSheet';
import { ReactionPicker } from '../components/chat/ReactionPicker';
import { rememberReactionAtom } from '../store/emoji';
import { nextReaction, submitReaction } from '../lib/reactions';
import { PinnedBanner } from '../components/chat/PinnedBanner';
import type { MessageActions } from '../components/chat/MessageBubble';
import { ImageLightbox } from '../components/chat/ImageLightbox';
import { albumItems } from '../lib/albums';
import { castVote } from '../lib/polls';
import {
  applyGatheringEvent,
  gatheringErrorMessage,
  withMyReply,
} from '../lib/gatherings';
import type {
  GatheringReply,
  GatheringState,
  PollState,
} from '@twinden/shared';
import { ReportSheet, type ReportTarget } from '../components/chat/ReportSheet';
import { ConfirmSheet } from '../components/ConfirmSheet';
import { useBlocks, useOnBlocksChanged } from '../hooks/useBlocks';
import { visibleConversations } from '../lib/blocks';
import { savedToast } from '../components/SavedToast';
import {
  onChangeRefused,
  onMessageSent,
  queueChange,
  reconcileOutbox,
} from '../lib/outbox';
import { unindexMessage } from '../lib/searchIndex';
import {
  KEYS_ARRIVED_EVENT,
  useDeviceNotices,
  useMemberJoins,
  useOwnDeviceNotice,
} from '../hooks/useE2EE';
import { ackOwnDeviceNotice, noteChatList } from '../lib/e2ee/session';
import { OwnDeviceNotice } from '../components/chat/OwnDeviceNotice';
import { SecurityCodeDialog } from '../components/chat/SecurityCodeDialog';
import { forwardSealed, isE2EEReady } from '../lib/e2ee/send';
import {
  saveSealedLink,
  saveSealedMessageDocument,
  type DocumentEnvelope,
} from '../lib/e2ee/apps';
import type { Envelope } from '../lib/e2ee/session';
import {
  ChatActionsSheet,
  MuteSheet,
  type MuteChoice,
} from '../components/chat/ChatActionsSheet';
import {
  badgeUnread,
  homeOrder,
  withMute,
  withSettings,
} from '../lib/chatSettings';
import { startPrivateReply } from '../lib/replyPrivately';
import { DisappearingSheet } from '../components/chat/DisappearingSheet';
import { durationLabel, withoutExpired } from '../lib/disappearing';

// What a forwarded copy carries: the opened envelope of an encrypted message,
// or an older plain message's text and file details (its file wasn't
// encrypted, so it's marked plainFile).
function forwardEnvelope(message: ChatMessage): Envelope | null {
  if (message.e2ee?.envelope) return message.e2ee.envelope;
  if (message.e2ee?.locked) return null;
  if (message.type === 'text') return { v: 1, text: message.content };
  const meta = (message.metadata ?? {}) as NonNullable<Envelope['meta']>;
  return {
    v: 1,
    meta: {
      fileName: meta.fileName,
      mimeType: meta.mimeType,
      fileSize: meta.fileSize,
      width: meta.width,
      height: meta.height,
      duration: meta.duration,
      waveform: meta.waveform,
      plainFile: true,
    },
  };
}
import {
  withNewMessage,
  type LiveMessage,
  conversationName,
  directChatWith,
  firstUrl,
  memberJoinNotices,
  type ChatMessage,
  type DeviceNotice,
} from '../components/chat/messageUtils';
import {
  currentFamilyIdAtom,
  familiesAtom,
  familiesLoadedAtom,
  memberLabelsAtom,
} from '../store';
import { useConversationCache } from '../hooks/useConversationCache';
import { findOccasion, type FestivalDayId } from '../lib/greetings';
import { t as tNow, useT } from '../i18n';

// "Wish them" opens a chat with the greeting sheet up: ?greet=birthday (D144).
const GREET_PARAM = 'greet';
// …and a festival day's cards leading: &greetDay=bhai-dooj (D315).
const GREET_DAY_PARAM = 'greetDay';

// Disappeared messages (D238) reload the list once per burst of events.
const DELETED_RELOAD_DELAY_MS = 2000;

// The open chat lives in the URL (`/?conversation=<id>`), the same link push
// notifications and Starred use. That gives the phone back button a history
// entry to pop: chat → chat list instead of leaving the app.
const CONVERSATION_PARAM = 'conversation';

interface ChatHistoryState {
  // Set when the chat was opened from the list, so going "back" can pop the
  // entry rather than push a new one.
  openedFromList?: boolean;
}

// The default group first (D10), then my pinned chats (D237), then the
// most recent.
const sortConversations = homeOrder;

function errorMessage(err: unknown, fallback?: string): string {
  return err instanceof ApiError
    ? err.message
    : (fallback ?? tNow('common.somethingWrong'));
}

export function ChatPage() {
  const memberLabels = useAtomValue(memberLabelsAtom);
  const currentFamilyId = useAtomValue(currentFamilyIdAtom);
  // The family's list from the store: back from another page it shows at
  // once; a switch shows nothing of the previous family (M66, D248).
  const {
    conversations: allConversations,
    cached: listCached,
    setConversations,
    setFor: setConversationsFor,
  } = useConversationCache(currentFamilyId);
  const currentUser = useAtomValue(currentUserAtom);
  // Direct chats with people I blocked stay out of sight (M58, D208).
  const { blockedIds, block } = useBlocks();
  const conversations = useMemo(
    () => visibleConversations(allConversations, blockedIds, currentUser?.id),
    [allConversations, blockedIds, currentUser?.id]
  );
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [blockTarget, setBlockTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const setOpenConversationId = useSetAtom(openConversationIdAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState<unknown>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [lightboxId, setLightboxId] = useState<string | null>(null);
  // The album item shown in the viewer (M60, D219).
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const messageListRef = useRef<MessageListHandle>(null);
  const toast = useToast();
  const t = useT();
  const newGroupDialog = useDisclosure();
  const newChatDialog = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    hasFamilies,
    currentRole,
    loading: familiesLoading,
    error: familiesError,
    loadFamilies,
  } = useFamily();

  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // A link to a chat outside the current family stays unresolved until the
  // conversation shows up in the list.
  const requestedId = searchParams.get(CONVERSATION_PARAM);
  const activeConversation =
    conversations.find((c) => c.id === requestedId) ?? null;
  const activeId = activeConversation?.id ?? null;
  const settingsConversation =
    conversations.find((c) => c.id === settingsId) ?? null;

  const openConversation = useCallback(
    (conversationId: string, extra?: Record<string, string>) => {
      if (conversationId === requestedId && !extra) return;
      const next = new URLSearchParams(searchParams);
      next.set(CONVERSATION_PARAM, conversationId);
      for (const [key, value] of Object.entries(extra ?? {})) {
        next.set(key, value);
      }
      // Switching between chats replaces the entry, so Back always returns
      // to the list rather than walking through every chat opened.
      const replacing = requestedId !== null;
      setSearchParams(next, {
        replace: replacing,
        state: replacing
          ? location.state
          : ({ openedFromList: true } satisfies ChatHistoryState),
      });
    },
    [requestedId, searchParams, setSearchParams, location.state]
  );

  const closeConversation = useCallback(() => {
    if ((location.state as ChatHistoryState | null)?.openedFromList) {
      navigate(-1);
      return;
    }
    // Arrived by deep link: there is no list entry behind us to pop.
    const next = new URLSearchParams(searchParams);
    next.delete(CONVERSATION_PARAM);
    setSearchParams(next, { replace: true });
  }, [location.state, navigate, searchParams, setSearchParams]);

  const markConversationRead = useCallback(
    (conversationId: string) => {
      setConversations((previous) =>
        previous.map((conversation) =>
          conversation.id === conversationId
            ? // Reading also ends "Mark as unread" (D232).
              { ...conversation, unreadCount: 0, markedUnread: false }
            : conversation
        )
      );
    },
    [setConversations]
  );

  const {
    messages,
    setMessages,
    replaceMessage,
    removeMessage,
    jumpTo,
    hasMore,
    loading: messagesLoading,
    loadError: messagesError,
    searchingUnread,
    jumping,
    loadingOlder,
    loadOlder,
    olderError,
    retryOlder,
    hasNewer,
    newerCount,
    loadingLatest,
    loadLatest,
    appendOwnMessage,
    reload: reloadMessages,
    unreadMarker,
  } = useConversationMessages(activeId, markConversationRead);

  // A family device shared this chat's key: open what was locked (M28).
  useEffect(() => {
    const onKeys = (event: Event) => {
      const { conversationId, all } = (
        event as CustomEvent<{ conversationId?: string; all?: boolean }>
      ).detail;
      // `all`: keys came while no socket event said which chat (D262).
      if (all || conversationId === activeId) reloadMessages();
    };
    window.addEventListener(KEYS_ARRIVED_EVENT, onKeys);
    return () => window.removeEventListener(KEYS_ARRIVED_EVENT, onKeys);
  }, [activeId, reloadMessages]);

  // M20: reply/edit mode, search, "seen by", delete confirmation.
  const [composerMode, setComposerMode] = useState<ComposerMode | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // Message info (M62, D227): by id, so the sheet follows live read events.
  const [infoMessageId, setInfoMessageId] = useState<string | null>(null);
  const infoMessage =
    messages.find(
      (message) => message.id === infoMessageId && !message.deletedAt
    ) ?? null;
  const [deleteTarget, setDeleteTarget] = useState<ChatMessage | null>(null);
  const isModerator = currentRole === 'root' || currentRole === 'admin';
  const typingUsers = useAtomValue(typingUsersAtom);

  // Modes belong to the chat they were started in. (A draft's reply comes
  // back through the composer once it's checked, D228.)
  useEffect(() => {
    setComposerMode(null);
    setSearchOpen(false);
  }, [activeId]);

  // Replying to something that's since been deleted: drop the reply (D228).
  useEffect(() => {
    if (
      composerMode?.kind === 'reply' &&
      replyTargetGone(composerMode.message.id, messages)
    ) {
      setComposerMode(null);
    }
  }, [composerMode, messages]);

  // This person's drafts, for the chat list (D228).
  const setDrafts = useSetAtom(draftsAtom);
  const currentUserId = currentUser?.id;
  useEffect(() => {
    if (!currentUserId) {
      setDrafts({});
      return;
    }
    // Sealed on the device (D281): opened once, then kept in memory.
    setDrafts(loadDrafts(currentUserId));
    let cancelled = false;
    void openDrafts(currentUserId).then((drafts) => {
      if (!cancelled) setDrafts(drafts);
    });
    return () => {
      cancelled = true;
    };
  }, [currentUserId, setDrafts]);

  // Drafts go with their chat or family: a chat that's no longer in this
  // family's list (left, removed, deleted), or a family I'm no longer in.
  const families = useAtomValue(familiesAtom);
  const familiesLoaded = useAtomValue(familiesLoadedAtom);
  useEffect(() => {
    if (!currentUserId || !currentFamilyId || conversations.length === 0) {
      return;
    }
    // Only a loaded list for this family is the whole truth about it.
    if (conversations.some((c) => c.familyId !== currentFamilyId)) return;
    const ids = new Set(conversations.map((c) => c.id));
    setDrafts(
      pruneDrafts(
        currentUserId,
        (id, draft) => draft.familyId !== currentFamilyId || ids.has(id)
      )
    );
  }, [conversations, currentFamilyId, currentUserId, setDrafts]);
  useEffect(() => {
    if (!currentUserId || !familiesLoaded) return;
    const familyIds = new Set(families.map((family) => family.id));
    setDrafts(
      pruneDrafts(
        currentUserId,
        (_id, draft) => !draft.familyId || familyIds.has(draft.familyId)
      )
    );
  }, [families, familiesLoaded, currentUserId, setDrafts]);

  const otherParticipantIds = useMemo(
    () =>
      (activeConversation?.participants ?? [])
        .map((participant) => participant.id)
        .filter((id) => id !== currentUser?.id),
    [activeConversation, currentUser?.id]
  );

  // "{Name}'s devices changed" (M73, D303): in every chat with someone this
  // device noticed gain a device, newest per person, on this device only.
  // A chat started after the change doesn't get it.
  const noticedAt = useDeviceNotices();
  // A new device on my own account, from this device's record (M73).
  const ownNoticeAt = useOwnDeviceNotice();
  // "{Name} was added to this chat" / "…joined the family" (D343): someone
  // who joined after this device first looked, on this device only.
  const joins = useMemberJoins();
  const deviceNotices = useMemo<DeviceNotice[]>(() => {
    if (!activeConversation) return [];
    const since = Date.parse(activeConversation.createdAt);
    const nameOf = (participant: { id: string; displayName: string }) =>
      memberLabels[participant.id] ??
      (participant.displayName.split(' ')[0] || participant.displayName);
    const devices = activeConversation.participants.flatMap((participant) => {
      const at = noticedAt[participant.id];
      if (participant.id === currentUser?.id || at === undefined) return [];
      if (Number.isFinite(since) && at < since) return [];
      return [{ userId: participant.id, name: nameOf(participant), at }];
    });
    return [
      ...devices,
      ...memberJoinNotices(activeConversation, joins, currentUser?.id, nameOf),
    ];
  }, [activeConversation, noticedAt, joins, currentUser?.id, memberLabels]);
  // Tapping the line shows that person's security code (D63).
  const [codePerson, setCodePerson] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const openDeviceNotice = useCallback(
    (userId: string) => {
      const person = activeConversation?.participants.find(
        (participant) => participant.id === userId
      );
      if (person) setCodePerson({ id: person.id, name: person.displayName });
    },
    [activeConversation]
  );

  // @mentions (M22, D27): handles for highlighting, people for the picker.
  const handleById = useMemo(
    () =>
      new Map(
        (activeConversation?.participants ?? []).map((p) => [p.id, p.handle])
      ),
    [activeConversation]
  );
  const mentionCandidates = useMemo(
    () =>
      (activeConversation?.participants ?? [])
        .filter((p) => p.id !== currentUser?.id)
        .map((p) => ({
          id: p.id,
          handle: p.handle,
          displayName: p.displayName,
          avatarUrl: p.avatarUrl,
        }))
        // Groups can mention the whole chat (M25, D43).
        .concat(
          activeConversation?.type === 'group'
            ? [
                {
                  id: EVERYONE_MENTION,
                  handle: EVERYONE_MENTION,
                  displayName: tNow('chat.page.everyoneInChat'),
                  avatarUrl: null,
                },
              ]
            : []
        ),
    [activeConversation, currentUser?.id]
  );

  // Who a gathering counts, and whether you may ring as a family admin (D286).
  const gatheringPeople = useMemo(
    () => ({
      ids: (activeConversation?.participants ?? []).map((p) => p.id),
      isAdmin: isModerator,
    }),
    [activeConversation, isModerator]
  );

  // Pinned messages (M22, D28): loaded per chat, refreshed when any message
  // in it changes (a pin, an unpin, or a pinned message being deleted).
  const [pins, setPins] = useState<ChatMessage[]>([]);
  const [forwardTarget, setForwardTarget] = useState<ChatMessage | null>(null);
  const [actionsTarget, setActionsTarget] = useState<ChatMessage | null>(null);
  // "＋" in the quick reaction row (M69, D264).
  const [reactionPickerTarget, setReactionPickerTarget] =
    useState<ChatMessage | null>(null);
  const rememberReaction = useSetAtom(rememberReactionAtom);
  const loadPins = useCallback(async (conversationId: string) => {
    try {
      const response = await api.getPins(conversationId);
      if (activeIdRef.current === conversationId) {
        setPins(response.data as ChatMessage[]);
      }
    } catch {
      // The banner is optional; the chat still works.
    }
  }, []);
  useEffect(() => {
    setPins([]);
    if (activeId) void loadPins(activeId);
  }, [activeId, loadPins]);

  const typingNames = useMemo(() => {
    if (!activeConversation) return [];
    const ids = (typingUsers[activeConversation.id] ?? []) as string[];
    return ids
      .filter((id) => id !== currentUser?.id)
      .map(
        (id) =>
          activeConversation.participants.find((p) => p.id === id)
            ?.displayName ?? tNow('chat.page.someone')
      );
  }, [typingUsers, activeConversation, currentUser?.id]);

  // Scroll to a message, loading the window around it first if needed
  // (reply quotes and search results).
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
  const jumpToMessage = useCallback(
    async (messageId: string, createdAt?: string) => {
      if (messageListRef.current?.scrollToMessage(messageId)) return;
      if (!createdAt) return;
      const ok = await jumpTo(messageId, createdAt);
      if (ok) {
        setPendingScrollId(messageId);
      } else {
        toast({
          title: tNow('chat.page.tooFarBack'),
          status: 'info',
        });
      }
    },
    [jumpTo, toast]
  );
  useEffect(() => {
    if (!pendingScrollId) return;
    // After the jumped-to window renders.
    const frame = requestAnimationFrame(() => {
      if (messageListRef.current?.scrollToMessage(pendingScrollId)) {
        setPendingScrollId(null);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingScrollId, messages]);

  const reactToMessage = useCallback(
    async (message: ChatMessage, emoji: string) => {
      const mine = message.reactions?.find((r) => r.userId === currentUser?.id);
      const next = nextReaction(mine?.emoji, emoji);
      // Optimistic; the broadcast confirms (or the error reverts).
      const optimistic = (reactions: ChatMessage['reactions']) => [
        ...(reactions ?? []).filter((r) => r.userId !== currentUser?.id),
        ...(next && currentUser
          ? [{ userId: currentUser.id, emoji: next }]
          : []),
      ];
      setMessages((list) =>
        list.map((entry) =>
          entry.id === message.id
            ? { ...entry, reactions: optimistic(entry.reactions) }
            : entry
        )
      );
      try {
        // The quick row shows your recent reactions (D264), remembered once
        // the server took it (or it was queued offline, D69).
        await submitReaction({
          messageId: message.id,
          conversationId: message.conversationId,
          emoji,
          next,
          send: (id, value) => api.reactToMessage(id, value),
          queue: queueChange,
          remember: rememberReaction,
        });
      } catch (err) {
        replaceMessage(message);
        toast({
          title: tNow('chat.page.reactFailed'),
          description: errorMessage(err),
          status: 'error',
        });
      }
    },
    [currentUser, replaceMessage, setMessages, toast, rememberReaction]
  );

  const confirmDelete = useCallback(async () => {
    const target = deleteTarget;
    if (!target) return;
    setDeleteTarget(null);
    try {
      await api.deleteMessage(target.id);
      replaceMessage({
        ...target,
        content: '',
        metadata: null,
        reactions: [],
        starredByMe: false,
        deletedAt: new Date().toISOString(),
      });
      if (composerMode?.message.id === target.id) setComposerMode(null);
    } catch (err) {
      toast({
        title: tNow('chat.page.deleteFailed'),
        description: errorMessage(err),
        status: 'error',
      });
    }
  }, [deleteTarget, replaceMessage, composerMode, toast]);

  // Phones show an open chat full-screen, without the app header and tabs.
  useEffect(() => {
    setOpenConversationId(activeId);
  }, [activeId, setOpenConversationId]);
  useEffect(() => () => setOpenConversationId(null), [setOpenConversationId]);

  // The family a load was for: an answer that arrives after a switch is
  // kept for its own family and never touches the one on screen (D248).
  const loadingFamilyRef = useRef(currentFamilyId);
  loadingFamilyRef.current = currentFamilyId;
  const loadConversations = useCallback(async () => {
    const familyId = currentFamilyId;
    if (!familyId) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.getConversations(familyId);
      if (response.success) {
        // Each chat's participants as the list shows them: the keys check
        // reads these, not the owners a keys answer lists (M73, D301); and
        // a family's first list names the chats that may have a first look
        // on a device from before M73 (D304).
        void noteChatList(
          familyId,
          response.data.map((conversation) => ({
            id: conversation.id,
            userIds: conversation.participants.map((p) => p.id),
          }))
        );
        setConversationsFor(
          familyId,
          sortConversations(response.data as ChatConversation[])
        );
        if (loadingFamilyRef.current === familyId) setListError(null);
      }
    } catch (error) {
      // The list column says so and offers Try again (D249); a list from
      // before stays on screen.
      if (loadingFamilyRef.current === familyId) setListError(error);
    } finally {
      if (loadingFamilyRef.current === familyId) setIsLoading(false);
    }
  }, [currentFamilyId, setConversationsFor]);

  // Load the selected family's conversations (D8: switching re-scopes).
  useEffect(() => {
    if (familiesLoading) return;

    if (!currentFamilyId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setListError(null);
    void loadConversations();
  }, [currentFamilyId, familiesLoading, loadConversations]);

  // The mark in the list column while this family's chats load for the
  // first time (M66, D248); a list already held for the family (coming back
  // from another page) shows at once.
  // The families list is part of the wait: until it has loaded (or failed)
  // there may be no family to ask for. A failed families load is a failed
  // home, with Try again fetching both (D249).
  const familiesPending = !familiesLoaded && !familiesError;
  const listState = useLoadingState({
    loading: isLoading || familiesLoading || familiesPending,
    hasData: listCached,
    error: listError ?? (familiesLoaded ? null : familiesError),
    retry: () => {
      setListError(null);
      setIsLoading(true);
      // Families first when they never loaded: the load effect then fetches
      // the list for whichever family is current, once, and the mark stays
      // up meanwhile (familiesLoading).
      if (!familiesLoaded) void loadFamilies();
      else void loadConversations();
    },
  });

  // A block or unblock from another tab or device: fetch the list again, so
  // an unblocked chat comes back (M58, D208).
  useOnBlocksChanged(loadConversations);

  // Family roster for the group / DM pickers and group settings.
  useEffect(() => {
    if (!currentFamilyId) {
      setFamilyMembers([]);
      return;
    }

    let cancelled = false;
    api
      .getFamily(currentFamilyId)
      .then((response) => {
        if (!cancelled) setFamilyMembers(response.data.members);
      })
      .catch(() => {
        if (!cancelled) setFamilyMembers([]);
      });

    return () => {
      cancelled = true;
    };
  }, [currentFamilyId]);

  // Conversation selection is per-family — clear it on switch (D8). Only on
  // an actual switch, so a deep link survives the initial family load.
  const previousFamilyIdRef = useRef(currentFamilyId);
  useEffect(() => {
    const previous = previousFamilyIdRef.current;
    previousFamilyIdRef.current = currentFamilyId;
    if (previous === null || previous === currentFamilyId) return;
    if (!searchParams.has(CONVERSATION_PARAM)) return;

    const next = new URLSearchParams(searchParams);
    next.delete(CONVERSATION_PARAM);
    setSearchParams(next, { replace: true });
  }, [currentFamilyId, searchParams, setSearchParams]);

  // Messages for every chat arrive over the socket; keep the list's order and
  // unread counts live. The open chat is marked read by its own hook.
  const { onEvent, connectionEpoch, onWake } = useWebSocket();

  // Previews and unread counts missed while the socket was down or the app
  // was in the background (2026-10-22): refresh the chat list when it
  // reconnects or comes back to the foreground.
  const seenEpochRef = useRef(connectionEpoch);
  useEffect(() => {
    if (connectionEpoch === seenEpochRef.current) return;
    const firstConnect = seenEpochRef.current === 0;
    seenEpochRef.current = connectionEpoch;
    if (!firstConnect && currentFamilyId) void loadConversations();
  }, [connectionEpoch, currentFamilyId, loadConversations]);

  // The app woke: back to the front, from the bfcache, focus, online, or a
  // tapped notification (issue #75). Refresh the list now, without waiting
  // for the socket to come back. A notification for the chat already open
  // doesn't change the address, so nothing else would move it: go to its
  // newest message (the ↓ path, which loads the newest page when the window
  // is far back, D271); its hook fetches the newest page meanwhile.
  useEffect(
    () =>
      onWake((wake) => {
        if (currentFamilyId) void loadConversations();
        if (
          wake.reason === 'notification' &&
          wake.conversationId &&
          wake.conversationId === activeIdRef.current
        ) {
          void messageListRef.current?.jumpToLatest();
        }
      }),
    [onWake, currentFamilyId, loadConversations]
  );
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        const conversationId = (payload as { conversationId?: string })
          .conversationId;
        if (
          event === WS_EVENTS.MESSAGE_UPDATED &&
          conversationId &&
          conversationId === activeIdRef.current
        ) {
          void loadPins(conversationId);
        }
      }),
    [onEvent, loadPins]
  );
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  // Someone left the family or deleted their account (M51): chats lose a
  // person, and a DM with a deleted account is named after it.
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.FAMILY_MEMBER_LEFT) return;
        const familyId = (payload as { familyId?: string }).familyId;
        if (familyId && familyId === currentFamilyId) void loadConversations();
      }),
    [onEvent, currentFamilyId, loadConversations]
  );

  // Home kept showing the message before the one you'd just sent
  // (2026-10-22). Your own messages now come back as message:sent (D206);
  // leaving a chat still reloads the list as a catch-up, since the server
  // knows the true latest message.
  const previousActiveRef = useRef(activeId);
  useEffect(() => {
    const left = previousActiveRef.current;
    previousActiveRef.current = activeId;
    if (left && left !== activeId && currentFamilyId) void loadConversations();
  }, [activeId, currentFamilyId, loadConversations]);
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        // Your own messages from any of your devices move the list too, but
        // never count as unread (D206).
        const own = event === WS_EVENTS.MESSAGE_SENT;
        if (event !== WS_EVENTS.MESSAGE_RECEIVED && !own) return;
        const message = payload as LiveMessage;
        setConversations((previous) => {
          const updated = withNewMessage(previous, message, {
            activeId: activeIdRef.current,
            currentUserId: currentUser?.id ?? null,
            own,
          });
          return updated === previous ? previous : sortConversations(updated);
        });
      }),
    [onEvent, currentUser?.id, setConversations]
  );

  // Unread @mentions for the pill on home (M25, D42): refreshed when the
  // family or the open chat changes, and when a new mention arrives.
  const [unreadMentions, setUnreadMentions] = useState(0);
  const refreshMentions = useCallback(() => {
    if (!currentFamilyId) return;
    api
      .getUnreadMentions(currentFamilyId)
      .then((response) => setUnreadMentions(response.data.count))
      .catch(() => undefined);
  }, [currentFamilyId]);
  useEffect(() => {
    refreshMentions();
  }, [refreshMentions, activeId]);
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.MESSAGE_RECEIVED) return;
        const message = payload as {
          mentions?: string[];
          mentionsEveryone?: boolean;
        };
        if (
          message.mentionsEveryone ||
          (currentUser && message.mentions?.includes(currentUser.id))
        ) {
          refreshMentions();
        }
      }),
    [onEvent, currentUser, refreshMentions]
  );

  // Opening a message from search or Mentions: open its chat, then jump to
  // it once the chat has loaded. Deep links carry it as ?message=&at=.
  const [jumpRequest, setJumpRequest] = useState<{
    conversationId: string;
    messageId: string;
    createdAt: string;
  } | null>(null);
  useEffect(() => {
    const messageId = searchParams.get('message');
    const createdAt = searchParams.get('at');
    if (!messageId || !createdAt || !requestedId) return;
    setJumpRequest({ conversationId: requestedId, messageId, createdAt });
    const next = new URLSearchParams(searchParams);
    next.delete('message');
    next.delete('at');
    setSearchParams(next, { replace: true, state: location.state });
  }, [searchParams, requestedId, setSearchParams, location.state]);
  useEffect(() => {
    if (!jumpRequest || activeId !== jumpRequest.conversationId) return;
    if (!messages.some((message) => message.conversationId === activeId))
      return;
    setJumpRequest(null);
    void jumpToMessage(jumpRequest.messageId, jumpRequest.createdAt);
  }, [jumpRequest, activeId, messages, jumpToMessage]);

  // Keep the app icon badge in step with the unread total while the app is open.
  useEffect(() => {
    // A chat marked as unread counts as one (D232); one kept archived
    // doesn't count (D306).
    const total = conversations.reduce(
      (sum, conversation) => sum + badgeUnread(conversation),
      0
    );
    updateAppBadge(total);
  }, [conversations]);

  // Per-chat notification mute (M17 follow-up), now for 8 hours, a week or
  // always (D233). Only silences push and sounds; the chat still updates and
  // unread counts still count. Loaded app-wide by useAlerts, which also
  // honors these mutes in-app.
  const [pushPreferences, setPushPreferences] = useAtom(pushPreferencesAtom);
  const mutedChatIds = pushPreferences
    ? liveMutes(pushPreferences).mutedConversationIds
    : [];
  // Draw again when the next timed mute ends, so its bell goes (D233).
  const [muteTick, setMuteTick] = useState(0);
  useEffect(() => {
    const now = Date.now();
    const ends = Object.values(pushPreferences?.mutedConversationUntil ?? {})
      .map((iso) => Date.parse(iso))
      .filter((end) => end > now);
    if (ends.length === 0) return;
    // setTimeout can't wait longer than ~24.8 days; a week fits.
    const wait = Math.min(Math.min(...ends) - now + 50, 2 ** 31 - 1);
    const timer = setTimeout(() => setMuteTick((tick) => tick + 1), wait);
    return () => clearTimeout(timer);
  }, [pushPreferences, muteTick]);

  // My own settings for a chat (M63): archive (D231), the unread mark
  // (D232) and mute (D233). Shown at once; put back if the server says no.
  const applySettings = useCallback(
    (settings: ChatSettings) => {
      setConversations((previous) => {
        const next = withSettings(previous, settings);
        return next === previous ? previous : sortConversations(next);
      });
      setPushPreferences((previous) =>
        previous ? withMute(previous, settings) : previous
      );
    },
    [setPushPreferences, setConversations]
  );
  const changeSettings = useCallback(
    async (
      conversationId: string,
      change: {
        archived?: boolean;
        keepArchived?: boolean;
        markedUnread?: boolean;
        mute?: MuteChoice | null;
        pinned?: boolean;
      },
      done?: string
    ) => {
      const before = allConversationsRef.current.find(
        (c) => c.id === conversationId
      );
      const previousPrefs = pushPreferencesRef.current;
      if (before) {
        setConversations((previous) =>
          sortConversations(
            previous.map((c) =>
              c.id !== conversationId
                ? c
                : {
                    ...c,
                    // A pinned chat isn't archived, and the other way round
                    // (D237).
                    ...(change.pinned !== undefined
                      ? {
                          pinnedAt: change.pinned
                            ? (c.pinnedAt ?? new Date().toISOString())
                            : null,
                          ...(change.pinned ? { archivedAt: null } : {}),
                        }
                      : {}),
                    ...(change.archived !== undefined
                      ? {
                          archivedAt: change.archived
                            ? (c.archivedAt ?? new Date().toISOString())
                            : null,
                          ...(change.archived ? { pinnedAt: null } : {}),
                        }
                      : {}),
                    ...(change.keepArchived !== undefined
                      ? { keepArchived: change.keepArchived }
                      : {}),
                    ...(change.markedUnread !== undefined
                      ? {
                          markedUnread: change.markedUnread,
                          ...(change.markedUnread ? {} : { unreadCount: 0 }),
                        }
                      : {}),
                  }
            )
          )
        );
      }
      try {
        const response = await api.updateChatSettings(conversationId, change);
        applySettings(response.data);
        if (done) toast({ title: done, status: 'success', duration: 2000 });
      } catch (err) {
        if (before) {
          setConversations((previous) =>
            sortConversations(
              previous.map((c) => (c.id === conversationId ? before : c))
            )
          );
        }
        if (previousPrefs) setPushPreferences(previousPrefs);
        toast(
          err instanceof ApiError && err.code === 'PIN_LIMIT'
            ? {
                title: tNow('chat.chatActions.pinLimit', {
                  n: CHAT_PIN_LIMIT,
                }),
                status: 'info',
              }
            : {
                title: tNow('chat.chatActions.failed'),
                description: errorMessage(err),
                status: 'error',
              }
        );
      }
    },
    [applySettings, setPushPreferences, toast, setConversations]
  );
  const deletedReloadRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (deletedReloadRef.current) clearTimeout(deletedReloadRef.current);
    },
    []
  );
  const allConversationsRef = useRef(allConversations);
  allConversationsRef.current = allConversations;
  // The open group whose members just changed (#97): if the fresh list no
  // longer has it (I left, was removed, or it was deleted), it closes.
  const membersChangedRef = useRef<string | null>(null);
  const pushPreferencesRef = useRef(pushPreferences);
  pushPreferencesRef.current = pushPreferences;

  // Other devices (and the server, when a message un-archives a chat) tell
  // this one about my settings; everyone in a group hears about its info.
  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event === WS_EVENTS.CONVERSATION_SETTINGS) {
          applySettings(payload as ChatSettings);
          return;
        }
        if (event === WS_EVENTS.CONVERSATION_UPDATED) {
          const info = payload as {
            conversationId: string;
            name: string | null;
            avatarUrl: string | null;
            description: ChatConversation['description'];
            disappearingSeconds?: number | null;
          };
          setConversations((previous) =>
            previous.map((c) =>
              c.id === info.conversationId
                ? {
                    ...c,
                    name: info.name,
                    avatarUrl: info.avatarUrl,
                    description: info.description,
                    // M64 (D238); older servers don't send it.
                    ...(info.disappearingSeconds !== undefined
                      ? { disappearingSeconds: info.disappearingSeconds }
                      : {}),
                  }
                : c
            )
          );
        }
        if (event === WS_EVENTS.CONVERSATION_MEMBERS) {
          // Who is in a group changed (#97): the list (and the open chat's
          // members) come from the server again; a chat I'm no longer in
          // leaves it.
          const { conversationId, familyId } =
            payload as WsConversationMembersPayload;
          if (familyId !== currentFamilyId) return;
          if (conversationId === activeIdRef.current) {
            membersChangedRef.current = conversationId;
          }
          void loadConversations();
          return;
        }
        if (event === WS_EVENTS.MESSAGE_DELETED) {
          // Disappeared messages (D238): the tile's preview and the pins
          // may have been one of them; the server knows what's left.
          const { conversationId, messageIds } =
            payload as WsMessageDeletedPayload;
          // Out of the pinned banner at once (D238).
          setPins((list) => list.filter((pin) => !messageIds.includes(pin.id)));
          if (conversationId === activeIdRef.current) {
            void loadPins(conversationId);
          }
          if (
            allConversationsRef.current.some((c) => c.id === conversationId)
          ) {
            // The sweep tells each chat separately, all at once: one
            // reload for the burst, not one per chat.
            if (deletedReloadRef.current) {
              clearTimeout(deletedReloadRef.current);
            }
            deletedReloadRef.current = setTimeout(() => {
              deletedReloadRef.current = null;
              void loadConversations();
            }, DELETED_RELOAD_DELAY_MS);
          }
        }
      }),
    [
      onEvent,
      applySettings,
      loadPins,
      loadConversations,
      setConversations,
      currentFamilyId,
    ]
  );

  useEffect(() => {
    const changed = membersChangedRef.current;
    if (!changed || isLoading) return;
    membersChangedRef.current = null;
    if (
      changed === activeIdRef.current &&
      !allConversations.some((c) => c.id === changed)
    ) {
      setSettingsId(null);
      closeConversation();
    }
  }, [allConversations, isLoading, closeConversation]);

  // Disappearing messages (D238): for everyone in the chat. Shown at once;
  // the server's line and conversation:updated confirm it.
  const [disappearingOpen, setDisappearingOpen] = useState(false);
  const changeDisappearing = useCallback(
    async (conversationId: string, duration: DisappearingDuration) => {
      const before = allConversationsRef.current.find(
        (c) => c.id === conversationId
      );
      const seconds =
        duration === 'off' ? null : DISAPPEARING_SECONDS[duration];
      const setSeconds = (value: number | null | undefined) =>
        setConversations((previous) =>
          previous.map((c) =>
            c.id === conversationId ? { ...c, disappearingSeconds: value } : c
          )
        );
      setSeconds(seconds);
      try {
        const response = await api.setDisappearing(conversationId, duration);
        setSeconds(response.data.disappearingSeconds);
        toast({
          title: response.data.disappearingSeconds
            ? tNow('chat.disappearing.turnedOn', {
                time: durationLabel(response.data.disappearingSeconds),
              })
            : tNow('chat.disappearing.turnedOff'),
          status: 'success',
          duration: 2500,
        });
      } catch (err) {
        setSeconds(before?.disappearingSeconds ?? null);
        toast({
          title: tNow('chat.disappearing.failed'),
          description: errorMessage(err),
          status: 'error',
        });
      }
    },
    [toast, setConversations]
  );

  // Opening a chat marked as unread clears the mark. With unread messages
  // the read call does it (after the unread divider is placed, D229).
  // Once per opening, so a refusal doesn't retry in a loop.
  const clearedMarkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeConversation) {
      clearedMarkRef.current = null;
      return;
    }
    if (!activeConversation.markedUnread) return;
    if ((activeConversation.unreadCount ?? 0) > 0) return;
    if (clearedMarkRef.current === activeConversation.id) return;
    clearedMarkRef.current = activeConversation.id;
    void changeSettings(activeConversation.id, { markedUnread: false });
  }, [activeConversation, changeSettings]);

  const [chatActionsId, setChatActionsId] = useState<string | null>(null);
  const [muteTargetId, setMuteTargetId] = useState<string | null>(null);
  const chatActionsTarget =
    conversations.find((c) => c.id === chatActionsId) ?? null;
  const muteTarget = conversations.find((c) => c.id === muteTargetId) ?? null;
  const chatLabel = (conversation: ChatConversation | null) =>
    !conversation
      ? ''
      : conversation.isDefault
        ? t('chat.header.everyone')
        : conversationName(conversation, currentUser?.id, memberLabels);
  const muteChat = (conversationId: string, choice: MuteChoice | null) =>
    void changeSettings(
      conversationId,
      { mute: choice },
      choice ? t('chat.mute.done') : t('chat.mute.undone')
    );

  const toggleStar = useCallback(
    async (message: ChatMessage) => {
      try {
        if (message.starredByMe) {
          await api.unstarMessage(message.id);
        } else {
          await api.starMessage(message.id);
        }
        setMessages((current) =>
          current.map((entry) =>
            entry.id === message.id
              ? { ...entry, starredByMe: !message.starredByMe }
              : entry
          )
        );
      } catch (err) {
        toast({
          title: tNow(
            message.starredByMe
              ? 'chat.page.unstarFailed'
              : 'chat.page.starFailed'
          ),
          description: errorMessage(err, tNow('chat.page.unknownError')),
          status: 'error',
        });
      }
    },
    [setMessages, toast]
  );

  const saveToFamilyLinks = useCallback(
    async (message: ChatMessage) => {
      if (!currentFamilyId) return;
      try {
        // Encrypted: the device reads the link and seals it for the family
        // (M28, D65); the server never sees it.
        const link = firstUrl(message.content);
        const response =
          isE2EEReady() && link
            ? await saveSealedLink(currentFamilyId, {
                url: link,
                messageId: message.id,
              })
            : await api.addFamilyLink(currentFamilyId, {
                messageId: message.id,
              });
        savedToast(
          toast,
          response.data.alreadySaved
            ? tNow('chat.page.alreadyInLinks')
            : tNow('chat.page.savedToLinks'),
          {
            action: {
              label: tNow('chat.page.view'),
              onClick: () => navigate('/links'),
            },
          }
        );
      } catch (err) {
        toast({
          title: tNow('chat.page.saveLinkFailed'),
          description: errorMessage(err, tNow('chat.page.unknownError')),
          status: 'error',
        });
      }
    },
    [currentFamilyId, navigate, toast]
  );

  const saveToFamilyDocuments = useCallback(
    async (message: ChatMessage) => {
      if (!currentFamilyId) return;
      try {
        if (isE2EEReady()) {
          // The chat's file is reused; only its details are sealed again,
          // for the family (M28, D65).
          const meta = (message.e2ee?.envelope?.meta ??
            message.metadata ??
            {}) as {
            fileName?: string;
            mimeType?: string;
            fileSize?: number;
            file?: DocumentEnvelope['file'];
            thumb?: DocumentEnvelope['thumb'];
          };
          await saveSealedMessageDocument(currentFamilyId, message.id, {
            fileName: meta.fileName ?? tNow('chat.message.attachment'),
            mimeType: meta.mimeType ?? 'application/octet-stream',
            fileSize: meta.fileSize ?? 0,
            ...(meta.file
              ? { file: meta.file, thumb: meta.thumb }
              : { plainFile: true }),
          });
        } else {
          await api.addFamilyDocument(currentFamilyId, {
            messageId: message.id,
          });
        }
        savedToast(toast, tNow('chat.page.savedToDocuments'), {
          action: {
            label: tNow('chat.page.view'),
            onClick: () => navigate('/documents'),
          },
        });
      } catch (err) {
        toast({
          title: tNow('chat.page.saveDocumentFailed'),
          description: errorMessage(err, tNow('chat.page.unknownError')),
          status: 'error',
        });
      }
    },
    [currentFamilyId, navigate, toast]
  );

  const togglePin = useCallback(
    async (message: ChatMessage) => {
      try {
        const response = await api.setPinned(message.id, !message.pinnedAt);
        setPins(response.data as ChatMessage[]);
        const pinnedIds = new Set(
          (response.data as ChatMessage[]).map((pin) => pin.id)
        );
        // Reflect it at once; the broadcast confirms it for everyone.
        setMessages((list) =>
          list.map((entry) =>
            entry.id === message.id || entry.pinnedAt
              ? {
                  ...entry,
                  pinnedAt: pinnedIds.has(entry.id)
                    ? (entry.pinnedAt ?? new Date().toISOString())
                    : null,
                }
              : entry
          )
        );
      } catch (err) {
        toast({
          title: tNow(
            message.pinnedAt ? 'chat.page.unpinFailed' : 'chat.page.pinFailed'
          ),
          description: errorMessage(err),
          status: 'error',
        });
      }
    },
    [setMessages, toast]
  );

  // Polls (M60, D220): a vote shows at once and is undone if the server
  // refuses it or can't be reached (votes aren't queued offline).
  const votePoll = useCallback(
    async (message: ChatMessage, choices: number[] | null) => {
      const before = message.poll;
      if (!before || !currentUser) return;
      const setPoll = (update: (current: PollState) => PollState) =>
        setMessages((list) =>
          list.map((entry) =>
            entry.id === message.id && entry.poll
              ? { ...entry, poll: update(entry.poll) }
              : entry
          )
        );
      let failure: unknown;
      const taken = await castVote({
        before,
        userId: currentUser.id,
        choices,
        setPoll,
        send: async () => {
          try {
            const response = choices
              ? await api.votePoll(message.id, choices)
              : await api.retractPollVote(message.id);
            return response.data.poll;
          } catch (err) {
            failure = err;
            throw err;
          }
        },
      });
      if (!taken) {
        toast({
          title: tNow('chat.poll.voteFailed'),
          description: errorMessage(failure),
          status: 'error',
        });
      }
    },
    [currentUser, setMessages, toast]
  );

  const closePoll = useCallback(
    async (message: ChatMessage) => {
      try {
        const response = await api.closePoll(message.id);
        setMessages((list) =>
          list.map((entry) =>
            entry.id === message.id
              ? { ...entry, poll: response.data.poll }
              : entry
          )
        );
      } catch (err) {
        toast({
          title: tNow('chat.poll.closeFailed'),
          description: errorMessage(err),
          status: 'error',
        });
      }
    },
    [setMessages, toast]
  );

  // Gatherings (M71, D282–D288): an answer shows at once and is undone if the
  // server refuses it; the other actions show what the server answers.
  const setGathering = useCallback(
    (messageId: string, gathering: GatheringState) =>
      setMessages((list) =>
        applyGatheringEvent(list, { messageId, gathering })
      ),
    [setMessages]
  );

  const replyToGathering = useCallback(
    async (
      message: ChatMessage,
      reply: GatheringReply,
      lateMinutes?: number
    ) => {
      const before = message.gathering;
      if (!before || !currentUser) return;
      setGathering(
        message.id,
        withMyReply(before, currentUser.id, reply, lateMinutes)
      );
      try {
        setGathering(
          message.id,
          await api.replyToGathering(message.id, {
            reply,
            ...(reply === 'late' && lateMinutes ? { lateMinutes } : {}),
          })
        );
      } catch (err) {
        setGathering(message.id, before);
        toast({
          title: tNow('chat.gathering.replyFailed'),
          description: gatheringErrorMessage(err),
          status: 'error',
        });
      }
    },
    [currentUser, setGathering, toast]
  );

  const takeBackGatheringReply = useCallback(
    async (message: ChatMessage) => {
      const before = message.gathering;
      if (!before || !currentUser) return;
      setGathering(message.id, withMyReply(before, currentUser.id, null));
      try {
        setGathering(message.id, await api.takeBackGatheringReply(message.id));
      } catch (err) {
        setGathering(message.id, before);
        toast({
          title: tNow('chat.gathering.replyFailed'),
          description: gatheringErrorMessage(err),
          status: 'error',
        });
      }
    },
    [currentUser, setGathering, toast]
  );

  const gatheringAction = useCallback(
    (
      call: (messageId: string) => Promise<GatheringState>,
      failed: 'arriveFailed' | 'ringFailed' | 'cancelFailed'
    ) =>
      async (message: ChatMessage) => {
        try {
          setGathering(message.id, await call(message.id));
        } catch (err) {
          toast({
            title: tNow(`chat.gathering.${failed}`),
            description: gatheringErrorMessage(err),
            status: 'error',
          });
        }
      },
    [setGathering, toast]
  );

  // Voters by what you call them (D143), else their name.
  const nameOf = useCallback(
    (userId: string) =>
      memberLabels[userId] ??
      familyMembers.find((member) => member.userId === userId)?.user
        .displayName ??
      tNow('chat.unknown'),
    [familyMembers, memberLabels]
  );

  const forwardMessage = useCallback(
    async (message: ChatMessage, conversationIds: string[]) => {
      try {
        // With this device's key every copy is sealed for its target chat
        // here; the server only passes it on (M28, D61).
        const envelope = forwardEnvelope(message);
        const response =
          isE2EEReady() && envelope
            ? await forwardSealed(
                message.id,
                conversationIds,
                envelope,
                message.type
              )
            : await api.forwardMessage(message.id, conversationIds, undefined);
        for (const copy of response.data as ChatMessage[]) {
          if (copy.conversationId === activeIdRef.current)
            appendOwnMessage(copy);
        }
        toast({
          title:
            conversationIds.length === 1
              ? tNow('chat.page.forwarded')
              : tNow('chat.page.forwardedTo', { n: conversationIds.length }),
          status: 'success',
          duration: 2000,
        });
      } catch (err) {
        toast({
          title: tNow('chat.page.forwardFailed'),
          description: errorMessage(err),
          status: 'error',
        });
        throw err;
      }
    },
    [appendOwnMessage, toast]
  );

  const hideForMe = useCallback(async () => {
    const target = deleteTarget;
    if (!target) return;
    setDeleteTarget(null);
    try {
      await api.hideMessage(target.id);
      removeMessage(target.id);
      setComposerMode((mode) => (mode?.message.id === target.id ? null : mode));
      void unindexMessage(target.id);
      setPins((list) => list.filter((pin) => pin.id !== target.id));
    } catch (err) {
      toast({
        title: tNow('chat.page.deleteFailed'),
        description: errorMessage(err),
        status: 'error',
      });
    }
  }, [deleteTarget, removeMessage, toast]);

  // Text sent through the outbox (M23) lands here once the server has it;
  // anything already in the loaded chat must have arrived (D34).
  useEffect(() => onMessageSent(appendOwnMessage), [appendOwnMessage]);
  useEffect(
    () =>
      onChangeRefused((entry) =>
        toast({
          title:
            entry.kind === 'edit'
              ? tNow('chat.page.editRefused')
              : tNow('chat.page.reactionRefused'),
          status: 'error',
        })
      ),
    [toast]
  );
  useEffect(() => {
    if (!activeId) return;
    reconcileOutbox(
      activeId,
      messages.flatMap((message) =>
        message.clientId ? [message.clientId] : []
      )
    );
  }, [activeId, messages]);

  const blessingWords = useAtomValue(blessingWordsAtom);
  const actions = useMemo<MessageActions>(
    () => ({
      toggleStar: (message) => void toggleStar(message),
      saveLink: (message) => void saveToFamilyLinks(message),
      saveDocument: (message) => void saveToFamilyDocuments(message),
      openImage: (message, index) => {
        setLightboxIndex(index ?? 0);
        setLightboxId(message.id);
      },
      reply: (message) => setComposerMode({ kind: 'reply', message }),
      edit: (message) => setComposerMode({ kind: 'edit', message }),
      remove: setDeleteTarget,
      react: (message, emoji) => void reactToMessage(message, emoji),
      showInfo: (message) => setInfoMessageId(message.id),
      togglePin: (message) => void togglePin(message),
      forward: setForwardTarget,
      openActions: setActionsTarget,
      pickReaction: setReactionPickerTarget,
      report: (message) => {
        if (!currentFamilyId || !message.sender) return;
        setReportTarget({
          familyId: currentFamilyId,
          person: {
            id: message.sender.id,
            name: message.sender.displayName,
          },
          conversationId: message.conversationId,
          messageId: message.id,
        });
      },
      jumpTo: (messageId, createdAt) =>
        void jumpToMessage(messageId, createdAt),
      replyPrivately: (message) => void replyPrivatelyRef.current(message),
      // Aashirwad (D280): goes out like a typed reply.
      bless: (message) => {
        sendBlessing(message, blessingWords);
        messageListRef.current?.scrollToBottom('smooth');
      },
      votePoll: (message, choices) => void votePoll(message, choices),
      closePoll: (message) => void closePoll(message),
      replyToGathering: (message, reply, lateMinutes) =>
        void replyToGathering(message, reply, lateMinutes),
      takeBackGatheringReply: (message) => void takeBackGatheringReply(message),
      arriveAtGathering: (message) =>
        void gatheringAction(
          (id) => api.arriveAtGathering(id),
          'arriveFailed'
        )(message),
      ringGathering: (message) =>
        void gatheringAction(
          (id) => api.ringGathering(id),
          'ringFailed'
        )(message),
      cancelGathering: (message) =>
        void gatheringAction(
          (id) => api.cancelGathering(id),
          'cancelFailed'
        )(message),
      gatheringPeople,
      nameOf,
    }),
    [
      blessingWords,
      votePoll,
      closePoll,
      replyToGathering,
      takeBackGatheringReply,
      gatheringAction,
      gatheringPeople,
      nameOf,
      toggleStar,
      saveToFamilyLinks,
      saveToFamilyDocuments,
      reactToMessage,
      jumpToMessage,
      togglePin,
      currentFamilyId,
    ]
  );

  // The other person in the open direct chat, for Report and Block (D207,
  // D208).
  const directPartner =
    activeConversation?.type === 'direct'
      ? (activeConversation.participants.find(
          (p) => p.id !== currentUser?.id
        ) ?? null)
      : null;

  const confirmBlock = async () => {
    if (!blockTarget) return;
    const person = blockTarget;
    setBlockTarget(null);
    try {
      await block(person.id);
      // The chat leaves the list, so the open view closes with it.
      closeConversation();
      toast({
        title: t('chat.block.done', { name: person.name }),
        status: 'success',
      });
    } catch (err) {
      toast({
        title: t('chat.block.failed'),
        description: errorMessage(err),
        status: 'error',
      });
    }
  };

  const selectSearchHit = (hit: SearchHit) => {
    setSearchOpen(false);
    void jumpToMessage(hit.id, hit.createdAt);
  };

  // The nav's "+" starts a new chat here (Living Room).
  useNavAction(t('chat.dialogs.newChat'), newChatDialog.onOpen);

  // Reply privately (D235): the sender's direct chat, opened (or started)
  // with the message quoted as text in its box, through the drafts (D228).
  const replyPrivately = async (message: ChatMessage) => {
    const sender = message.sender;
    if (!sender || !currentUserId || !currentFamilyId) return;
    const result = await startPrivateReply({
      message,
      conversations,
      blockedIds,
      familyId: currentFamilyId,
      userId: currentUserId,
      senderName:
        memberLabels[sender.id] ?? sender.displayName.split(' ')[0] ?? '',
    });
    if (result.kind === 'blocked') {
      toast({ title: t('chat.replyPrivately.blocked'), status: 'error' });
      return;
    }
    if (result.kind === 'failed') {
      toast({
        title: t('chat.replyPrivately.failed'),
        description: errorMessage(result.error),
        status: 'error',
      });
      return;
    }
    setDrafts(result.drafts);
    if (result.created) await loadConversations();
    openConversation(result.conversationId);
  };
  const replyPrivatelyRef = useRef(replyPrivately);
  replyPrivatelyRef.current = replyPrivately;

  // Tapping a face opens that person's chat, starting it if needed.
  const openPerson = async (userId: string, extra?: Record<string, string>) => {
    const existing = directChatWith(conversations, userId);
    if (existing) {
      openConversation(existing.id, extra);
      return;
    }
    if (!currentFamilyId) return;
    try {
      const response = await api.createFamilyConversation(currentFamilyId, {
        type: 'direct',
        userId,
      });
      await openCreated(response.data.id, extra);
    } catch (err) {
      toast({
        title: t('chat.dialogs.couldNotOpenChat'),
        description: errorMessage(err),
        status: 'error',
      });
    }
  };

  const openCreated = async (
    conversationId: string,
    extra?: Record<string, string>
  ) => {
    await loadConversations();
    openConversation(conversationId, extra);
  };

  // The greeting sheet opens once; the param goes so Back doesn't reopen it.
  const greetOccasion = activeConversation
    ? (findOccasion(searchParams.get(GREET_PARAM))?.id ?? null)
    : null;
  const greetDay = greetOccasion
    ? (searchParams.get(GREET_DAY_PARAM) as FestivalDayId | null)
    : null;
  const clearGreet = useCallback(() => {
    if (!searchParams.has(GREET_PARAM)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(GREET_PARAM);
    next.delete(GREET_DAY_PARAM);
    setSearchParams(next, { replace: true, state: location.state });
  }, [searchParams, setSearchParams, location.state]);

  // "Message Ravi", "Message Sunday lunch crew" (Living Room).
  const messagePlaceholder = () => {
    if (!activeConversation) return '';
    if (activeConversation.type === 'group') {
      return activeConversation.name
        ? t('chat.page.messageInGroup', { name: activeConversation.name })
        : t('chat.page.messageGroup');
    }
    const name =
      (mentionCandidates[0] && memberLabels[mentionCandidates[0].id]) ??
      mentionCandidates[0]?.displayName.split(' ')[0];
    return name
      ? t('chat.page.messageTo', { name })
      : t('chat.page.messageThem');
  };

  // Only once the list has been fetched: before that it's empty for
  // everyone, and "not invited yet" must not flash first (D248).
  if (familiesLoaded && !familiesLoading && !hasFamilies) {
    return (
      <Center h="full" px={6}>
        <VStack spacing={3} textAlign="center">
          <Home size={32} />
          <Text fontWeight="semibold">{t('chat.page.notInvited')}</Text>
          <Text color="gray.500" fontSize="sm">
            {t('chat.page.askRoot')}
          </Text>
        </VStack>
      </Center>
    );
  }

  // The viewer shows one photo; an album's is the item tapped (D219).
  const lightboxMessage =
    messages.find((message) => message.id === lightboxId) ?? null;
  const lightboxItems =
    lightboxMessage?.type === 'album' ? albumItems(lightboxMessage) : [];
  const lightboxItem =
    lightboxMessage?.type === 'album'
      ? (lightboxItems[Math.min(lightboxIndex, lightboxItems.length - 1)] ??
        null)
      : lightboxMessage;

  return (
    <Flex h="full">
      {/* Sidebar - Conversations List */}
      <Box
        w={{ base: 'full', md: '320px' }}
        flexShrink={0}
        borderRightWidth={{ base: 0, md: '1px' }}
        borderColor={borderColor}
        bg="canvas"
        display={{ base: activeConversation ? 'none' : 'flex', md: 'flex' }}
        flexDirection="column"
      >
        {ownNoticeAt !== null && (
          <OwnDeviceNotice
            onOpen={() => {
              void ackOwnDeviceNotice();
              navigate('/profile');
            }}
            onDismiss={() => void ackOwnDeviceNotice()}
          />
        )}
        {listState.show ? (
          <LoadingPanel
            state={listState}
            label={t('common.loading.chats')}
            flex="1"
          />
        ) : (
          <ConversationList
            conversations={conversations}
            activeId={activeId}
            currentUserId={currentUser?.id}
            members={familyMembers}
            mutedIds={mutedChatIds}
            onSelect={openConversation}
            onOpenPerson={(userId) => void openPerson(userId)}
            onWishPerson={(userId) =>
              void openPerson(userId, { [GREET_PARAM]: 'birthday' })
            }
            onWishSibling={(userId, params) => void openPerson(userId, params)}
            onNewGroup={newGroupDialog.onOpen}
            familyId={currentFamilyId}
            onOpenMessage={(conversationId, messageId, createdAt) => {
              setJumpRequest({ conversationId, messageId, createdAt });
              openConversation(conversationId);
            }}
            unreadMentions={unreadMentions}
            onOpenMentions={() => navigate('/mentions')}
            onChatActions={setChatActionsId}
          />
        )}
      </Box>

      {/* Main Chat Area */}
      <Flex
        flex="1"
        minW={0}
        direction="column"
        bg="canvas"
        display={{ base: activeConversation ? 'flex' : 'none', md: 'flex' }}
      >
        {activeConversation ? (
          <Flex direction="column" flex="1" minH={0} position="relative">
            {searchOpen && (
              <ConversationSearch
                conversationId={activeConversation.id}
                onClose={() => setSearchOpen(false)}
                onSelect={selectSearchHit}
              />
            )}
            <ChatHeader
              conversation={activeConversation}
              currentUserId={currentUser?.id}
              isMuted={isChatMuted(pushPreferences, activeConversation.id)}
              mutedUntil={muteEndsAt(pushPreferences, activeConversation.id)}
              canOpenGroupInfo={
                activeConversation.type === 'group' &&
                (!activeConversation.isDefault || isModerator)
              }
              typingNames={typingNames}
              onSearch={() => setSearchOpen(true)}
              onBack={closeConversation}
              onMute={() => setMuteTargetId(activeConversation.id)}
              onUnmute={() => muteChat(activeConversation.id, null)}
              onOpenSettings={() => setSettingsId(activeConversation.id)}
              onDisappearing={
                // Any member of a group or either person in a direct chat;
                // "Everyone" is for family roots and admins (D238).
                !activeConversation.isDefault || isModerator
                  ? () => setDisappearingOpen(true)
                  : undefined
              }
              onReport={
                directPartner && currentFamilyId
                  ? () =>
                      setReportTarget({
                        familyId: currentFamilyId,
                        person: {
                          id: directPartner.id,
                          name: directPartner.displayName,
                        },
                        conversationId: activeConversation.id,
                      })
                  : undefined
              }
              onBlock={
                directPartner
                  ? () =>
                      setBlockTarget({
                        id: directPartner.id,
                        name: directPartner.displayName,
                      })
                  : undefined
              }
            />

            <PinnedBanner
              // A pin that has disappeared goes with it (D238).
              pins={withoutExpired(pins)}
              onJump={(pin) => void jumpToMessage(pin.id, pin.createdAt)}
            />

            <MessageList
              ref={messageListRef}
              conversationId={activeConversation.id}
              familyId={activeConversation.familyId}
              messages={messages}
              currentUserId={currentUser?.id}
              hasMore={hasMore}
              loading={messagesLoading}
              loadError={messagesError}
              onRetry={reloadMessages}
              loadingOlder={loadingOlder}
              searchingUnread={searchingUnread}
              jumping={jumping}
              onLoadOlder={() => void loadOlder()}
              olderError={olderError}
              onRetryOlder={retryOlder}
              hasNewer={hasNewer}
              newerCount={newerCount}
              onLoadLatest={loadLatest}
              loadingLatest={loadingLatest}
              isGroup={activeConversation.type === 'group'}
              otherParticipantIds={otherParticipantIds}
              handleById={handleById}
              actions={actions}
              unreadMarker={unreadMarker}
              deviceNotices={deviceNotices}
              onDeviceNotice={openDeviceNotice}
            />

            <MessageComposer
              key={activeConversation.id}
              conversationId={activeConversation.id}
              mode={composerMode}
              onCancelMode={() => setComposerMode(null)}
              onSent={appendOwnMessage}
              onEdited={replaceMessage}
              onFocus={() => messageListRef.current?.scrollToBottom('smooth')}
              mentionCandidates={mentionCandidates}
              recipientName={
                activeConversation.type === 'direct' && mentionCandidates[0]
                  ? (memberLabels[mentionCandidates[0].id] ??
                    mentionCandidates[0].displayName.split(' ')[0])
                  : undefined
              }
              greetOccasion={greetOccasion}
              greetDay={greetDay}
              onGreetOpened={clearGreet}
              familyId={activeConversation.familyId}
              loadedMessages={messages}
              onRestoreReply={(message) =>
                setComposerMode((mode) =>
                  mode ? mode : { kind: 'reply', message }
                )
              }
              placeholder={
                activeConversation.isDefault
                  ? t('chat.page.sayToEveryone')
                  : messagePlaceholder()
              }
            />
          </Flex>
        ) : (
          <Center flex="1">
            <Text fontSize="xl" color="gray.500">
              {t('chat.page.select')}
            </Text>
          </Center>
        )}
      </Flex>

      {currentFamilyId && (
        <>
          <NewGroupDialog
            isOpen={newGroupDialog.isOpen}
            onClose={newGroupDialog.onClose}
            familyId={currentFamilyId}
            members={familyMembers}
            currentUserId={currentUser?.id ?? ''}
            onCreated={openCreated}
          />
          <NewChatDialog
            isOpen={newChatDialog.isOpen}
            onClose={newChatDialog.onClose}
            familyId={currentFamilyId}
            members={familyMembers}
            currentUserId={currentUser?.id ?? ''}
            onCreated={openCreated}
          />
          <GroupSettingsDialog
            isOpen={settingsId !== null}
            onClose={() => setSettingsId(null)}
            conversation={settingsConversation}
            members={familyMembers}
            currentUserId={currentUser?.id ?? ''}
            familyRole={currentRole ?? 'member'}
            onChanged={() => {
              void loadConversations();
            }}
            onClosed={() => {
              setSettingsId(null);
              closeConversation();
            }}
          />
        </>
      )}

      {currentUser && (
        <SecurityCodeDialog
          me={{ id: currentUser.id, name: currentUser.displayName }}
          them={codePerson}
          isOpen={codePerson !== null}
          onClose={() => setCodePerson(null)}
        />
      )}
      <ReportSheet
        isOpen={reportTarget !== null}
        target={reportTarget}
        onClose={() => setReportTarget(null)}
        onBlocked={(userId) => {
          if (directPartner?.id === userId) closeConversation();
        }}
      />
      <ConfirmSheet
        centered
        isOpen={blockTarget !== null}
        onClose={() => setBlockTarget(null)}
        icon={<Ban size={28} />}
        title={t('chat.block.title', { name: blockTarget?.name ?? '' })}
        body={t('chat.block.body')}
        confirmLabel={t('chat.block.confirm')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={() => void confirmBlock()}
      />
      <ImageLightbox
        message={lightboxItem}
        isOwn={lightboxMessage?.sender?.id === currentUser?.id}
        album={
          lightboxMessage?.type === 'album'
            ? {
                count: lightboxItems.length,
                index: Math.min(lightboxIndex, lightboxItems.length - 1),
                onIndex: setLightboxIndex,
              }
            : undefined
        }
        chatTitle={
          activeConversation
            ? activeConversation.isDefault
              ? t('chat.header.everyone')
              : conversationName(
                  activeConversation,
                  currentUser?.id,
                  memberLabels
                )
            : ''
        }
        onClose={() => setLightboxId(null)}
        onSaveDocument={actions.saveDocument}
        onToggleStar={() =>
          lightboxMessage && actions.toggleStar(lightboxMessage)
        }
        onShowInChat={(message) => {
          setLightboxId(null);
          actions.jumpTo(message.id, message.createdAt);
        }}
      />
      <MessageInfoSheet
        message={infoMessage}
        participants={activeConversation?.participants ?? []}
        currentUserId={currentUser?.id}
        familyId={activeConversation?.familyId}
        isGroup={activeConversation?.type === 'group'}
        onClose={() => setInfoMessageId(null)}
      />
      <DeleteMessageDialog
        message={deleteTarget}
        isOwn={deleteTarget?.sender?.id === currentUser?.id}
        familyId={activeConversation?.familyId}
        canDeleteForEveryone={
          deleteTarget?.sender?.id === currentUser?.id || isModerator
        }
        onCancel={() => setDeleteTarget(null)}
        onDeleteForEveryone={() => void confirmDelete()}
        onDeleteForMe={() => void hideForMe()}
      />
      <MessageActionSheet
        message={actionsTarget}
        currentUserId={currentUser?.id}
        isGroup={activeConversation?.type === 'group'}
        otherParticipantCount={otherParticipantIds.length}
        actions={actions}
        onClose={() => setActionsTarget(null)}
      />
      <ReactionPicker
        message={reactionPickerTarget}
        onReact={(message, emoji) =>
          // The message as it is now: someone may have reacted meanwhile.
          void reactToMessage(
            messages.find((entry) => entry.id === message.id) ?? message,
            emoji
          )
        }
        onClose={() => setReactionPickerTarget(null)}
      />
      <ChatActionsSheet
        conversation={chatActionsTarget}
        name={chatLabel(chatActionsTarget)}
        muted={
          chatActionsTarget
            ? isChatMuted(pushPreferences, chatActionsTarget.id)
            : false
        }
        mutedUntil={
          chatActionsTarget
            ? muteEndsAt(pushPreferences, chatActionsTarget.id)
            : null
        }
        onClose={() => setChatActionsId(null)}
        onMarkUnread={(unread) => {
          if (!chatActionsTarget) return;
          // Marking the open chat unread leaves it, or opening would
          // clear the mark straight away (D232).
          if (unread && chatActionsTarget.id === activeId) closeConversation();
          void changeSettings(chatActionsTarget.id, { markedUnread: unread });
        }}
        onMute={() =>
          chatActionsTarget && setMuteTargetId(chatActionsTarget.id)
        }
        onUnmute={() =>
          chatActionsTarget && muteChat(chatActionsTarget.id, null)
        }
        onArchive={(archived) =>
          chatActionsTarget &&
          void changeSettings(
            chatActionsTarget.id,
            { archived },
            archived
              ? t('chat.chatActions.archived')
              : t('chat.chatActions.unarchived')
          )
        }
        onKeepArchived={(keepArchived) =>
          chatActionsTarget &&
          void changeSettings(chatActionsTarget.id, { keepArchived })
        }
        onPin={(pinned) =>
          chatActionsTarget &&
          void changeSettings(chatActionsTarget.id, { pinned })
        }
      />
      <DisappearingSheet
        isOpen={disappearingOpen && activeConversation !== null}
        seconds={activeConversation?.disappearingSeconds}
        onClose={() => setDisappearingOpen(false)}
        onPick={(duration) =>
          activeConversation &&
          void changeDisappearing(activeConversation.id, duration)
        }
      />
      <MuteSheet
        isOpen={muteTarget !== null}
        name={chatLabel(muteTarget)}
        onClose={() => setMuteTargetId(null)}
        onPick={(choice) => muteTarget && muteChat(muteTarget.id, choice)}
      />
      <ForwardDialog
        message={forwardTarget}
        conversations={conversations}
        currentUserId={currentUser?.id}
        onClose={() => setForwardTarget(null)}
        onForward={forwardMessage}
      />
    </Flex>
  );
}
